import { useState, useEffect, useMemo, useCallback } from 'react';
import { getPreviewUrl } from '../../../../../../../services/attachmentService';

/**
 * Hook for managing preview data loading and version comparison state
 */
export const usePreviewData = (missionId, file, initialCompareVersion) => {
  // Preview data
  const [currentPreviewData, setCurrentPreviewData] = useState(null);
  const [comparePreviewData, setComparePreviewData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Comparison mode
  const [isComparing, setIsComparing] = useState(!!initialCompareVersion);
  const [compareIndex, setCompareIndex] = useState(0);

  // Get all versions except the current one (for comparison)
  const olderVersions = useMemo(() => {
    if (!file.versions || file.versions.length <= 1) return [];
    return file.versions.slice(1);
  }, [file.versions]);

  const hasVersions = olderVersions.length > 0;
  const currentCompareVersion = olderVersions[compareIndex];
  const totalVersions = file.versions?.length || 1;
  
  // Version number for the currently viewed older version
  const olderVersionNumber = totalVersions - compareIndex - 1;

  // Load current version preview
  useEffect(() => {
    const loadCurrentPreview = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const preview = await getPreviewUrl(missionId, file.id);
        setCurrentPreviewData(preview);
      } catch (err) {
        console.error('Failed to load preview:', err);
        setError('Failed to load preview');
      } finally {
        setIsLoading(false);
      }
    };

    loadCurrentPreview();
  }, [missionId, file.id]);

  // Load compare version preview when comparing
  useEffect(() => {
    if (!isComparing || !currentCompareVersion) {
      setComparePreviewData(null);
      return;
    }

    const loadComparePreview = async () => {
      try {
        const preview = await getPreviewUrl(missionId, currentCompareVersion.id);
        setComparePreviewData(preview);
      } catch (err) {
        console.error('Failed to load compare preview:', err);
        setComparePreviewData(null);
      }
    };

    loadComparePreview();
  }, [missionId, currentCompareVersion, isComparing]);

  // Set initial compare index if provided
  useEffect(() => {
    if (initialCompareVersion && olderVersions.length > 0) {
      const idx = olderVersions.findIndex(v => v.id === initialCompareVersion.id);
      if (idx >= 0) {
        setCompareIndex(idx);
        setIsComparing(true);
      }
    }
  }, [initialCompareVersion, olderVersions]);

  // Navigation handlers
  const goToPrevVersion = useCallback(() => {
    setCompareIndex(i => Math.max(0, i - 1));
  }, []);

  const goToNextVersion = useCallback(() => {
    setCompareIndex(i => Math.min(olderVersions.length - 1, i + 1));
  }, [olderVersions.length]);

  const toggleCompare = useCallback(() => {
    setIsComparing(prev => !prev);
    if (!isComparing) {
      setCompareIndex(0);
    }
  }, [isComparing]);

  return {
    // Data
    currentPreviewData,
    comparePreviewData,
    isLoading,
    error,
    // Version info
    olderVersions,
    hasVersions,
    currentCompareVersion,
    totalVersions,
    olderVersionNumber,
    // Comparison state
    isComparing,
    compareIndex,
    // Actions
    goToPrevVersion,
    goToNextVersion,
    toggleCompare,
  };
};
